import { Box, ButtonNew, ColorOptions, Loader, Spacer, TextNew } from '@jfs/ui-new'
import { isEmpty } from '@jfs/utils'
import { useNavigation, useRoute } from '@react-navigation/native'
import { observer } from 'mobx-react'
import { FC, useMemo, useState } from 'react'
import { ModBox } from '../../comps/ModifiedBox/ModifiedBox'
import NativeBreadcrumb from '../../comps/NativeBreadCrumb'
import { NewTable } from '../../comps/NewTable'
import {
  initiateRefundStatusDataV2,
  initiateRefundStatusDataV2Subscription,
  initiateRefundTitleDataV2Subscription
} from '../../data/static/data'
import { staticStrings } from '../../data/static/strings'
import { useLazyApiData, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import { getBreadCrumbArray, getCustomColumnWidth, useWindowDimensions } from '../../util/util'
import { styles } from './styles'

interface ButtonViewProps {
  handleNavigate: () => void
  handleRefund: () => void
  enableAction: boolean
  refundsComplete: boolean
  themedStyle: any
}

const ButtonView: FC<ButtonViewProps> = ({
  handleNavigate,
  handleRefund,
  enableAction,
  refundsComplete,
  themedStyle,
}: ButtonViewProps) => {
  return (
    <Box {...themedStyle.buttonContainer}>
      <ButtonNew
        testID="refundDoneBtn"
        hide={!refundsComplete}
        disabled={!enableAction}
        title={staticStrings.done}
        type={'primarySmall'}
        borderColor={'transparent'}
        borderRadius={'5px'}
        shadowType={'shadowPrimary'}
        onPress={handleNavigate}
        textType={'body12'}
      />
      <ButtonNew
        hide={refundsComplete}
        title={staticStrings.cancel}
        type={'custom'}
        color={'#FFFFFF'}
        hoverColor={'BRAND_COLOR_GRAY_6'}
        underlayColor={'BRAND_COLOR_GRAY_5'}
        borderColor={'transparent'}
        borderRadius={'5px'}
        textType={'body12'}
        titleColor={'BRAND_COLOR_GRAY_1'}
        shadowType={'shadowSecondary'}
        onPress={handleNavigate}
      />
      <Spacer size={10} direction="horizontal" hide={refundsComplete} />
      <ButtonNew
        testID="initiateRefundBtn"
        hide={refundsComplete}
        disabled={!enableAction}
        title={staticStrings.refundTransactions}
        type={'primarySmall'}
        borderColor={'transparent'}
        borderRadius={'5px'}
        shadowType={'shadowPrimary'}
        onPress={handleRefund}
        textType={'body12'}
      />
    </Box>
  )
}

const InitiateRefund: FC = () => {
  const {
    getSelectedTransactions,
    initiateRefunds,
    updateRefundRequested,
    getRefundStatus,
    getTxnRemainingAmtToRefund,
    updateSubscriptionRefundRequested,
    getSubscriptionTransactions,
    getSubscriptionRefundStatus,
    initiateSubscriptionRefunds,
    updateRefundInvoiceId,
  } = useStores((store) => ({
    getSelectedTransactions: store.transactions.getSelectedTransactions,
    initiateRefunds: store.transactions.initiateRefunds,
    updateRefundRequested: store.transactions.updateRefundRequested,
    updateRefundInvoiceId: store.transactions.updateRefundInvoiceId,
    getRefundStatus: store.transactions.getRefundStatus,
    getTxnRemainingAmtToRefund: store.transactions.getTxnRemainingAmtToRefund,
    updateSubscriptionRefundRequested: store.subscriptions.updateRefundRequested,
    getSubscriptionTransactions: store.subscriptions.getSelectedTransactions,
    getSubscriptionRefundStatus: store.subscriptions.getRefundStatus,
    initiateSubscriptionRefunds: store.subscriptions.initiateRefunds,
  }))

  const viewPortWidth = useWindowDimensions()
  const themedStyle = styles
  const [enableAction, setAction] = useState(false)
  const [invoiceIdValid, setInvoiceIdValid] = useState(false)
  const navigation = useNavigation()
  const location = useRoute().name

  let remainingSubscriptionAmount = useMemo(() => {
    if (location === Routes.SUBSCRIPTION_INITIATEREFUND) {
      const [, { amount = '', refundAmount = '' }] = getSubscriptionTransactions()
      return Number(amount.substring(2)) - Number(refundAmount.substring(2))
    }
    return undefined
  }, [getSubscriptionTransactions, location])

  const onChangeText = (rrnNumber: string, value: string): void => {
    let remainingAmount
    if (location === Routes.SUBSCRIPTION_INITIATEREFUND) {
      remainingAmount = remainingSubscriptionAmount
    } else {
      remainingAmount = getTxnRemainingAmtToRefund()
    }

    if (+value > +remainingAmount || +value === 0) {
      setAction(false)
    } else {
      setAction(true)
    }
    if (location === Routes.SUBSCRIPTION_INITIATEREFUND) {
      updateSubscriptionRefundRequested(rrnNumber, value)
      return
    }
    updateRefundRequested(value)
  }

  const onInvoiceIdChange = (value: string): void => {
    if (isEmpty(value) || value.length > 100) {
      setInvoiceIdValid(false)
    } else {
      setInvoiceIdValid(true)
    }
    updateRefundInvoiceId(value)
  }

  const bCrumbpath = location?.substring(1)
  let bCrumItem = getBreadCrumbArray(bCrumbpath as string)
  const breadCrumbItems = bCrumItem.map((item) => {
    const bcItem = item
    if (bcItem.to === 'home') {
      bcItem.label = 'Home'
      bcItem.to = Routes.DASHBOARD
    }
    return bcItem
  })

  const getListData = () => {
    if (state === 'fulfilled' || state === 'rejected') {
      if (location === Routes.SUBSCRIPTION_INITIATEREFUND) {
        return getSubscriptionRefundStatus()
      }
      return getRefundStatus()
    }

    if (location === Routes.SUBSCRIPTION_INITIATEREFUND) {
      return getSubscriptionTransactions()
    }
    return getSelectedTransactions()
  }

  const getListHeader = () => {
    if (state === 'fulfilled' || state === 'rejected') {
      if (location === Routes.SUBSCRIPTION_INITIATEREFUND) {
        return initiateRefundStatusDataV2Subscription
      }
      return initiateRefundStatusDataV2 //getRefundStatus()
    }
    if (location === Routes.SUBSCRIPTION_INITIATEREFUND) {
      return initiateRefundTitleDataV2Subscription //getSubscriptionTransactions()
    }
    return getSelectedTransactions()?.[0] //getSelectedTransactions()
  }

  const getInitiateRefundApi = () => {
    return location === Routes.SUBSCRIPTION_INITIATEREFUND ? initiateSubscriptionRefunds : initiateRefunds
  }

  const [initiateRefund, { state }] = useLazyApiData(getInitiateRefundApi())

  const handleRefund = (): void => {
    initiateRefund(listData)
  }

  const handleNavigate = (): void => {
    if (breadCrumbItems[0]) {
      navigation.navigate(breadCrumbItems[0].to)
      return
    }
    navigation.navigate(Routes.RECENTS)
  }

  // const getScrollViewStyle = (): ViewStyle => {
  //   if (viewPortWidth < 1100) {
  //     // overlap solution small devices
  //     return themedStyle.tableContainer
  //   }
  //   return themedStyle.emptyContainer
  // }
  // const scrollViewStyle = getScrollViewStyle()
  const isLoading = state === 'pending'
  const listData = getListData()
  const isSodexo = location !== Routes.SUBSCRIPTION_INITIATEREFUND && getListData()?.[1]?.paymentMode === 'Sodexo'
  return (
    <Box {...themedStyle.container}>
      <Box {...themedStyle.header}>
        {/* <Breadcrumb hide={!(breadCrumbItems?.length > 1)} crumbs={breadCrumbItems} /> */}
        <NativeBreadcrumb
          hide={!(breadCrumbItems.length > 1)}
          navigate={navigation.navigate}
          crumbs={breadCrumbItems}
        />
        <Spacer size={24} />
        <TextNew type={'titleBold20'} testID="title-refund" >{staticStrings.refundTransactions}</TextNew>
      </Box>
      <Spacer size={88} hide={!isLoading} />
      <ModBox show={isLoading}>
        <Loader type="small" />
        <Box justifyContent="center" pt={16} alignSelf="center" testID='refund-loader'>
          <TextNew textAlign="center" type="body12">
            {staticStrings.loadingMessage}
          </TextNew>
        </Box>
      </ModBox>
      <Spacer size={104} hide={!isLoading} />
      <ModBox f={1} margin={0} padding={0} show={!isLoading}>
        <ModBox margin={0} padding={16} {...themedStyle.tableContainer}>
          <NewTable
            data={listData.slice(1)}
            rowHeight={50}
            headerHeight={64}
            customWidthFunc={getCustomColumnWidth}
            tableWidth={viewPortWidth > 1200 ? viewPortWidth - 286 : viewPortWidth}
            headerRowKeys={getListHeader()}
            onChangeInput={onChangeText}
            onInvoiceChange={onInvoiceIdChange}
            onRowClick={() => null}
            transactionType={1}
          />
        </ModBox>
        <Box height={1} backgroundColor={'DISABLED_GREY' as ColorOptions} />
        <ButtonView
          handleNavigate={handleNavigate}
          handleRefund={handleRefund}
          enableAction={enableAction && (!isSodexo || invoiceIdValid)}
          refundsComplete={state === 'fulfilled'}
          themedStyle={themedStyle}
        />
      </ModBox>
    </Box>
  )
}

export default observer(InitiateRefund)
